import { Flex, Spacer } from "@chakra-ui/layout";
import { Button } from "@chakra-ui/react";
import React from "react";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { JsonEditor } from "src/scenes/admin/components/JsonEditor";
import { safeJsonParseWithSchema } from "src/utils/jsonValidation";
import { ImportMessageTemplatesSchema } from "../json-schema";
import { onImportCompleteCallback } from "../types";

type Props = {
  onBack: () => void;
  onComplete: onImportCompleteCallback;
  formTemplateId: string;
};

export const ImportFromJSON: React.FC<Props> = ({
  onBack,
  onComplete,
  formTemplateId,
}) => {
  const toast = useAvelaToast();
  const [json, setJson] = React.useState("");
  const [jsonSchema, setJsonSchema] = React.useState<object>({});

  const [isImporting, setImporting] = React.useState(false);
  const service = FSQ.useImportMessageTemplates();

  const handleImportFromJSON = React.useCallback(async () => {
    setImporting(true);
    toast.closeAll();

    try {
      if (json === "") {
        // Show a toast notification instead of calling onComplete
        toast.error({
          title: "JSON is required",
          description: "Please provide a valid JSON data to import",
        });
        return;
      }

      const importMessageTemplatesSchema = await import(
        "src/generated/import-message-templates-schema.json"
      );

      // Safely parse and validate JSON against schema before sending to API
      const parseResult =
        await safeJsonParseWithSchema<ImportMessageTemplatesSchema>(
          json,
          importMessageTemplatesSchema
        );

      if (!parseResult.success || !parseResult.data) {
        toast.error({
          title: "Invalid JSON",
          description: "Please check your JSON format",
        });
        return;
      }

      const { data: result } = await service.mutateAsync({
        path: { formTemplateId: formTemplateId },
        body: { messageTemplates: parseResult.data },
      });

      if (!result || !result?.data) {
        toast.error({
          title: "Something went wrong",
          description: "Please try again",
        });
        return;
      }

      if (result.data.errors.length > 0) {
        toast.error({
          title: "Some messages were not imported",
          description: "Some or all messages were not imported due to errors",
        });
        return onComplete({
          data: result.data.messageTemplates,
          errors: result.data.errors.map((error) => error.message),
        });
      }

      // Success
      toast({
        title: "Imported from JSON",
        description: "Automated messages imported from JSON successfully",
        status: "info",
        isClosable: true,
      });

      return onComplete({
        data: result.data.messageTemplates,
      });
    } catch (error: any) {
      if (error.error?.message) {
        toast.error({
          title: "Something went wrong",
          description: error.error.message,
        });
      } else {
        toast.genericError();
      }
    } finally {
      setImporting(false);
    }
  }, [toast, json, service, formTemplateId, onComplete]);

  React.useEffect(() => {
    (async () => {
      try {
        // Import the pre-generated form template schema
        const schema = await import(
          "src/generated/import-message-templates-schema.json"
        );
        setJsonSchema(schema.default || schema);
      } catch (error) {
        console.error(error);
        setJsonSchema({});
      }
    })();
  }, []);

  return (
    <Flex direction="column" gap={6} grow="1" height="100%">
      <JsonEditor
        schema={jsonSchema}
        text={json}
        onChange={setJson}
        filename="messageTemplates.json"
        editorHeight="fillParent"
      />
      <AdminFormButtons insideTabPanel>
        <Button
          variant="outline"
          colorScheme="gray"
          onClick={() => {
            onBack();
          }}
        >
          Back
        </Button>
        <Spacer />
        <Button isLoading={isImporting} onClick={handleImportFromJSON}>
          Import
        </Button>
      </AdminFormButtons>
    </Flex>
  );
};
